/** @format */
import isPropValid from '@emotion/is-prop-valid';
import React, { FunctionComponent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { MarketCode } from '@toucan/config/markets';

import { getConfig } from '@toucan/helpers/settings';

import Amex from '@toucan/images/amex.svg';
import ApplePay from '@toucan/images/apple-pay.svg';
import Bancontact from '@toucan/images/bancontact.svg';
import EPS from '@toucan/images/eps.svg';
import GooglePay from '@toucan/images/google-pay.svg';
import Ideal from '@toucan/images/ideal.svg';
import Mastercard from '@toucan/images/mastercard.svg';
import PaypalCredit from '@toucan/images/paypal-credit.svg';
import Paypal from '@toucan/images/paypal.svg';
import Visa from '@toucan/images/visa.svg';

import { SupportedCard } from '@toucan/client/global/nextjs';

import { isEdge, isIE } from './helpers';
import { styles } from './payment-methods.styles';

export enum PaymentMethodsColour {
    MIDNIGHT = 'midnight',
    WHITE = 'white',
}

export enum PaymentMethodsVariant {
    FULL = 'full',
    ONLY_CARDS = 'only-cards',
}

export enum PaymentMethodsSize {
    LARGE = 'large',
    SMALL = 'small',
}

export type PaymentMethodsProps = {
    market: MarketCode;

    colour?: PaymentMethodsColour;

    variant?: PaymentMethodsVariant;

    size?: PaymentMethodsSize;

    'data-testid'?: string;

    className?: string;
};

type PaymentIconKeys = SupportedCard | 'paypal' | 'paypalCredit';

type PaymentIcons = Record<PaymentIconKeys, FunctionComponent>;

type MappedPaymentIcons = {
    paymentIcon: PaymentIconKeys;
    details: FunctionComponent;
};

type AriaLabels = Record<PaymentIconKeys, string>;

const StyledPaymentMethods = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

const paymentIcons: PaymentIcons = {
    eps: EPS,
    bancontact: Bancontact,
    ideal: Ideal,
    applePay: ApplePay,
    googlePay: GooglePay,
    visa: Visa,
    mastercard: Mastercard,
    amex: Amex,
    paypal: Paypal,
    paypalCredit: PaypalCredit,
};

const ariaLabelMap: AriaLabels = {
    eps: 'EPS',
    bancontact: 'Bancontact',
    ideal: 'Ideal',
    applePay: 'ApplePay',
    googlePay: 'GooglePay',
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    paypal: 'Paypal',
    paypalCredit: 'PaypalCredit',
};

export const PaymentMethods: FunctionComponent<PaymentMethodsProps> = ({
    market,
    colour,
    variant = PaymentMethodsVariant.FULL,
    size = PaymentMethodsSize.LARGE,
    ...otherProps
}: PaymentMethodsProps) => {
    const { publicRuntimeConfig } = getConfig();
    const [svgRefs] = useState(new Map<string, HTMLLIElement | null>());
    const paypalEnabled = publicRuntimeConfig.paypalMarkets.includes(market);
    const paypalCreditEnabled =
        publicRuntimeConfig.paypalCreditMarkets.includes(market);

    const iconsToShow = (Object.keys(paymentIcons) as PaymentIconKeys[])
        .filter((icon) => {
            const allowedIcons: string[] = [
                ...(publicRuntimeConfig.supportedCards[market] ?? []),
            ];

            if (variant === PaymentMethodsVariant.FULL) {
                paypalEnabled && allowedIcons.push('paypal');
                paypalCreditEnabled && allowedIcons.push('paypalCredit');
            }

            return allowedIcons.includes(icon);
        })
        .map((paymentIcon): MappedPaymentIcons => {
            return {
                paymentIcon,
                details: paymentIcons[paymentIcon],
            };
        });

    /**
     * Fixes payment method svgs in IE by setting the width of the items
     * based from their viewBoxes
     */
    useEffect(() => {
        if (!isIE() && !isEdge()) {
            return;
        }

        const refs = [...svgRefs];
        let height = 0;

        refs.forEach((array: [string, HTMLLIElement | null]) => {
            const ref = array[1];

            if (ref === null) return;

            if (height === 0) {
                height = ref.getBoundingClientRect().height;
            }

            const svg = ref.querySelector('svg');
            if (svg == null) return;

            const viewBox = svg.getAttribute('viewBox');
            if (viewBox == null) return;

            const viewBoxParts = viewBox.split(' ');
            const viewBoxWidth = parseInt(viewBoxParts[2]);
            const viewBoxHeight = parseInt(viewBoxParts[3]);

            svg.style.height = `${height}px`;
            svg.style.width = `${(viewBoxWidth / viewBoxHeight) * height}px`;
        });
    }, [size]);

    return (
        <StyledPaymentMethods
            market={market}
            variant={variant}
            colour={colour}
            size={size}
            {...otherProps}
        >
            <ul className="payment-methods">
                {iconsToShow.map(
                    ({ details, paymentIcon }: MappedPaymentIcons) => {
                        const Icon = details;
                        const labelText = ariaLabelMap[paymentIcon] ?? '';
                        const className = `payment-icon payment-icon--${paymentIcon}`;
                        const dataTestId = `payment-icon--${paymentIcon}`;
                        return (
                            <li
                                aria-label={labelText}
                                key={paymentIcon}
                                className={className}
                                data-testid={dataTestId}
                                ref={(ref: HTMLLIElement | null) => {
                                    svgRefs.set(paymentIcon, ref);
                                }}
                            >
                                <Icon />
                            </li>
                        );
                    }
                )}
            </ul>
        </StyledPaymentMethods>
    );
};
